import { FC, useState, useEffect } from "react";
import {
  Button,
  ConfirmationDialog,
  nextSelectionAfterToggle,
  withoutWorkspace,
} from "@capacit-com/lara-law-shared-ui";
import type {
  WorkspaceSelectionEntry as SharedSelectionEntry,
  WorkspaceSelectionToggle as SharedSelectionToggle,
} from "@capacit-com/lara-law-shared-ui";
import { WorkspaceSelectionDialog } from "@/pages/chat/blocks/workspace-selection-dialog";
import type {
  WorkspaceDocument,
  WorkspaceFolder,
} from "@/models/workspace/Workspace";
import useWorkspace from "@/hooks/useWorkspace";

type SelectionEntry = {
  item: WorkspaceDocument | WorkspaceFolder;
  workspaceId: string;
};

/**
 * This picker's own selectability rule: **every** document is fair game, not
 * just the indexed and current ones chat needs. Passed to both the explorer and
 * the selection reducer so the checkboxes and the selection agree.
 */
const isDocumentSelectable = () => true;

export interface SelectedWorkspaceDocument {
  id: string;
  name: string;
  workspaceId: string;
  workspaceName: string;
}

interface WorkspaceDocumentPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (documents: SelectedWorkspaceDocument[]) => void;
  excludeIds?: string[];
}

const WorkspaceDocumentPickerDialog: FC<WorkspaceDocumentPickerDialogProps> = ({
  open,
  onOpenChange,
  onSelect,
  excludeIds = [],
}) => {
  const { useQueryAllWorkspaces } = useWorkspace();
  const { data: workspaces, isLoading } = useQueryAllWorkspaces();

  const [selectedItems, setSelectedItems] = useState<SelectionEntry[]>([]);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedItems([]);
    }
  }, [open]);

  const handleSelectItem = (
    item: WorkspaceDocument | WorkspaceFolder,
    workspaceId: string
  ) => {
    setSelectedItems(
      (prev) =>
        nextSelectionAfterToggle(
          prev as unknown as SharedSelectionEntry[],
          { item, workspaceId } as unknown as SharedSelectionToggle,
          isDocumentSelectable
        ) as unknown as SelectionEntry[]
    );
  };

  const handleDeselectWorkspace = (workspaceId: string) => {
    setSelectedItems(
      (prev) =>
        withoutWorkspace(
          prev as unknown as SharedSelectionEntry[],
          workspaceId
        ) as unknown as SelectionEntry[]
    );
  };

  const handleConfirm = () => {
    const documents = selectedItems
      .filter((s) => "activeRevision" in s.item)
      .filter((s) => !excludeIds.includes(s.item.id))
      .map((s) => {
        const doc = s.item as WorkspaceDocument;
        const workspace = workspaces?.find((ws) => ws.id === s.workspaceId);
        return {
          id: doc.id,
          name: doc.name,
          workspaceId: s.workspaceId,
          workspaceName: workspace?.name || "",
        };
      });

    onSelect(documents);
    onOpenChange(false);
  };

  const selectedDocumentCount = selectedItems.filter(
    (s) => "activeRevision" in s.item && !excludeIds.includes(s.item.id)
  ).length;

  // Every close path (Esc, outside click, ×, Annuller) lands here. A ticked
  // document is an unapplied choice, so ask before dropping it (LAR-726) — the
  // same guard FormDialog gives the legislation picker.
  const handleOpenChange = (next: boolean) => {
    if (!next && selectedDocumentCount > 0) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    onOpenChange(next);
  };

  return (
    <>
      <WorkspaceSelectionDialog
        open={open}
        onOpenChange={handleOpenChange}
        title="Vælg dokumenter fra workspaces"
        workspaces={workspaces ?? []}
        isLoading={isLoading}
        emptyMessage="Ingen workspaces fundet"
        selectedItems={selectedItems.map((s) => s.item)}
        onSelectItem={handleSelectItem}
        onDeselectWorkspace={handleDeselectWorkspace}
        isItemSelectable={isDocumentSelectable}
        onConfirm={handleConfirm}
        confirmLabel="Tilføj valgte"
        showSearch
        // h-[80vh] must stay: the explorer scrolls inside a flex-1 min-h-0 body,
        // which only gets a height when the dialog's own height is definite.
        // With max-h alone the body grows with the tree and overflow-hidden
        // clips the long workspaces instead of letting them scroll (LAR-583).
        contentClassName="sm:max-w-[900px] h-[80vh] max-h-[80vh] flex flex-col min-w-0 overflow-hidden"
        footer={
          <div className="flex items-center justify-between w-full">
            <span className="text-sm text-muted-foreground">
              {selectedDocumentCount} dokument
              {selectedDocumentCount !== 1 ? "er" : ""} valgt
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Annuller
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={selectedDocumentCount === 0}
              >
                Tilføj valgte
              </Button>
            </div>
          </div>
        }
      />
      <ConfirmationDialog
        isOpen={open && isDiscardConfirmOpen}
        onClose={() => setIsDiscardConfirmOpen(false)}
        onConfirm={() => {
          setIsDiscardConfirmOpen(false);
          onOpenChange(false);
        }}
        title="Kassér valget?"
        description="De dokumenter, du har valgt, bliver ikke tilføjet."
        confirmText="Kassér"
        cancelText="Fortsæt redigering"
        variant="destructive"
        discard
      />
    </>
  );
};

export default WorkspaceDocumentPickerDialog;
